import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, 
  FileText, 
  DollarSign, 
  TrendingUp,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Loader2,
  CheckCircle2,
  Edit,
  X,
  Trash2,
  AlertCircle,
  QrCode
} from 'lucide-react';
import { useParceiroDetails } from '@/hooks/useParceiros';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { EditarParceiro } from './EditarParceiro';
import { ParceiroDocumentosTab } from './ParceiroDocumentosTab';
import { ParceiroFinanceiroTab } from './ParceiroFinanceiroTab';
import { ParceiroRedeTab } from './ParceiroRedeTab';
import { ParceiroMetrics, ParceiroLead } from '@/types/parceiros';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { toast } from 'sonner';

// Função de formatação de telefone
const formatPhone = (phone: string | undefined) => {
  if (!phone) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '');
  // Formata no padrão brasileiro (00) 0000-0000
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  // Para números com 11 dígitos (celular com 9 dígitos)
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  // Retorna o número original se não conseguir formatar
  return phone;
};

interface ParceiroDetailsProps {
  parceiroId: string | null;
  onClose: () => void;
  onUpdate?: () => void;
}

export function ParceiroDetails({ parceiroId, onClose, onUpdate }: ParceiroDetailsProps) {
  console.log('🔍 ParceiroDetails montado com parceiroId:', parceiroId);
  const { parceiro, loading, error, refetch } = useParceiroDetails(parceiroId);
  console.log('🔍 ParceiroDetails - estado:', { parceiro: !!parceiro, loading, error });
  const permissions = usePermissions();
  const isMobile = useIsMobile();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hasLeadsDialogOpen, setHasLeadsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [leadsCount, setLeadsCount] = useState(0);
  const [parceiroLeads, setParceiroLeads] = useState<ParceiroLead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const isViewingSelf = permissions.userProfile?.id === parceiroId;
  // Aba Rede: visível se o parceiro visualizado tem pode_criar_rede_parceiros E:
  // 1. O usuário logado NÃO é um parceiro (admin/vendedor)
  // 2. OU o usuário logado É o próprio parceiro visualizado (auto-visualização do parceiro pai)
  const parceiroPodeCriarRede = parceiro?.parceiro?.pode_criar_rede_parceiros ?? false;
  const showRedeTab = parceiroPodeCriarRede && (!permissions.isPartner() || isViewingSelf);

  const handleEditSuccess = () => {
    refetch();
    if (onUpdate) onUpdate();
  };

  

  const handleDeleteClick = async () => {
    if (!parceiroId) return;

    // Verificar se o parceiro possui leads
    try {
      const response = await fetchWithAuth(`/api/parceiros/${parceiroId}/check-leads`);
      
      if (!response.ok) {
        throw new Error('Erro ao verificar leads do parceiro');
      }

      const data = await response.json();
      
      if (data.hasLeads) {
        setLeadsCount(data.leadsCount);
        setHasLeadsDialogOpen(true);
      } else {
        setDeleteDialogOpen(true);
      }
    } catch (error) {
      console.error('Erro ao verificar leads:', error);
      toast.error('Erro ao verificar se o parceiro possui leads');
    }
  };

  const handleConfirmDelete = async () => {
    if (!parceiroId) return;
    
    setIsDeleting(true);
    
    try {
      const response = await fetchWithAuth(`/api/parceiros/${parceiroId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao excluir parceiro');
      }

      toast.success('Parceiro excluído com sucesso!');
      setDeleteDialogOpen(false);
      onClose();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Erro ao excluir parceiro:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir parceiro');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Buscar leads do parceiro para a lista na aba informação geral
  useEffect(() => {
    if (!parceiroId) {
      setParceiroLeads([]);
      return;
    }
    let cancelled = false;
    setLeadsLoading(true);
    fetchWithAuth(`/api/parceiros/${parceiroId}/leads`)
      .then(async (res) => {
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      })
      .then((data: ParceiroLead[]) => {
        if (!cancelled) setParceiroLeads(data);
      })
      .catch(() => {
        if (!cancelled) setParceiroLeads([]);
      })
      .finally(() => {
        if (!cancelled) setLeadsLoading(false);
      });
    return () => { cancelled = true; };
  }, [parceiroId]);

  return (
    <Dialog open={!!parceiroId} onOpenChange={onClose}>
      <DialogContent
        className={`
          max-w-4xl max-h-[90vh] overflow-hidden
          flex flex-col p-4 sm:p-6
          max-md:inset-0 max-md:left-0 max-md:top-0 max-md:translate-x-0 max-md:translate-y-0
          max-md:h-[100dvh] max-md:max-h-[100dvh] max-md:rounded-none max-md:w-full max-md:max-w-none
        `}
      >
        <DialogHeader className="flex-shrink-0 space-y-0 pb-2 sm:pb-4">
          <div className="flex flex-col gap-3 max-sm:gap-2 sm:flex-row sm:items-center sm:justify-between">
            <DialogTitle className="text-base sm:text-lg pr-8">Detalhes do Parceiro</DialogTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {parceiro && !loading && (
                <>
                  <Button
                    variant="outline"
                    size={isMobile ? "icon" : "sm"}
                    onClick={() => setEditDialogOpen(true)}
                    title="Editar"
                    className="flex-shrink-0"
                  >
                    <Edit className="h-4 w-4 sm:mr-2" />
                    {!isMobile && 'Editar'}
                  </Button>
                  <Button
                    variant="outline"
                    size={isMobile ? "icon" : "sm"}
                    onClick={handleDeleteClick}
                    title="Excluir Parceiro"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                  >
                    <Trash2 className="h-4 w-4 sm:mr-2" />
                    {!isMobile && 'Excluir Parceiro'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 flex-shrink-0">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <div className="text-center py-12 flex-shrink-0">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {parceiro && (
          <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList
              className={`w-full mb-8 md:mb-6 grid gap-1 p-1 grid-cols-2 ${showRedeTab ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}
            >
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              {showRedeTab && <TabsTrigger value="rede">Rede</TabsTrigger>}
              <TabsTrigger value="documentos">Documentos</TabsTrigger>
              <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6 mt-6 md:mt-4">
              {/* Informações Básicas */}
              <Card>
              <CardHeader className="px-4 sm:px-6">
                <CardTitle className="text-base sm:text-lg">Informações do Parceiro</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 px-4 sm:px-6 pb-4 sm:pb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Users className="h-4 w-4 mr-2" />
                      Nome
                    </div>
                    <p className="font-medium break-words">{parceiro.parceiro.nome}</p>
                  </div>
                  
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Mail className="h-4 w-4 mr-2 shrink-0" />
                      Email
                    </div>
                    <p className="font-medium break-all">{parceiro.parceiro.email}</p>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Phone className="h-4 w-4 mr-2" />
                      Telefone
                    </div>
                    <p className="font-medium">{formatPhone(parceiro.parceiro.telefone) || 'Não informado'}</p>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4 mr-2" />
                      Localização
                    </div>
                    <p className="font-medium">
                      {parceiro.parceiro.cidade}/{parceiro.parceiro.estado}
                    </p>
                  </div>
                  
                  {parceiro.parceiro.pix && (
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <QrCode className="h-4 w-4 mr-2" />
                        Chave Pix
                      </div>
                      <p className="font-medium">{parceiro.parceiro.pix}</p>
                    </div>
                  )}
                  
                  <div className="space-y-1">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4 mr-2" />
                      Data de Início
                    </div>
                    <p className="font-medium">{formatDate(parceiro.parceiro.created_at)}</p>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Status
                    </div>
                    <Badge variant={parceiro.parceiro.ativo ? 'default' : 'secondary'}>
                      {parceiro.parceiro.ativo ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Métricas Gerais */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-[1fr_1fr_1fr_minmax(140px,1.5fr)]">
              <Card>
                <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total de Leads
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xl sm:text-2xl font-bold text-blue-600">
                        {parceiro.total_leads}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        +{parceiro.leads_30_dias} este mês
                      </p>
                    </div>
                    <Users className="h-8 w-8 text-blue-600/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Propostas Enviadas
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xl sm:text-2xl font-bold text-green-600 truncate">
                        {parceiro.propostas_enviadas}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatCurrency(parceiro.valor_propostas)}
                      </p>
                    </div>
                    <FileText className="h-8 w-8 text-green-600/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Contratos Fechados
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xl sm:text-2xl font-bold text-purple-600">
                        {parceiro.contratos_fechados}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        +{parceiro.contratos_30_dias} este mês
                      </p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-purple-600/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Valor em Contratos
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xl sm:text-2xl font-bold text-orange-600 whitespace-nowrap">
                        {formatCurrency(parceiro.valor_contratos)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Receita total
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-orange-600/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Histórico Mensal */}
            {parceiro.metrics && parceiro.metrics.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Histórico de Performance (12 meses)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {parceiro.metrics.map((metric, index) => {
                      const mesFormatado = new Date(metric.mes).toLocaleDateString('pt-BR', {
                        month: 'short',
                        year: 'numeric'
                      });
                      const hasLeads = metric.leads_nomes && metric.leads_nomes.length > 0;
                      
                      return (
                        <div key={index} className="p-3 bg-muted/50 rounded-md">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                              <div className="text-sm font-medium w-16 sm:w-20 shrink-0">{mesFormatado}</div>
                              <div className="flex flex-wrap gap-3 sm:gap-6 text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground">Leads:</span>
                                  <span className="font-semibold text-blue-600">{metric.leads}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Propostas:</span>
                                  <span className="font-semibold ml-1 text-green-600">{metric.propostas}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Contratos:</span>
                                  <span className="font-semibold ml-1 text-purple-600">{metric.contratos}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-sm font-semibold text-orange-600 shrink-0">
                              {formatCurrency(metric.valor_contratos)}
                            </div>
                          </div>
                          
                          {/* Lista de nomes dos leads */}
                          {hasLeads && (
                            <div className="mt-3 pt-3 border-t border-muted">
                              <div className="text-xs text-muted-foreground mb-2">Leads capturados:</div>
                              <div className="flex flex-wrap gap-1">
                                {metric.leads_nomes.map((nome, leadIndex) => (
                                  <Badge key={leadIndex} variant="secondary" className="text-xs">
                                    {nome}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lista de leads adicionados pelo parceiro */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Leads adicionados pelo parceiro</CardTitle>
              </CardHeader>
              <CardContent>
                {leadsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : parceiroLeads.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">Nenhum lead cadastrado por este parceiro.</p>
                ) : (
                  <div className="space-y-2">
                    <ul className="divide-y divide-border rounded-md border">
                      {parceiroLeads.map((lead) => (
                        <li key={lead.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 hover:bg-muted/50">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{lead.nome}</span>
                            {lead.tipos_produto && lead.tipos_produto.length > 0 && (
                              <span className="text-xs text-muted-foreground">
                                ({lead.tipos_produto.join(' · ')})
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-muted-foreground">
                            Adicionado em {formatDate(lead.created_at)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Taxas de Conversão */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Taxa Lead → Proposta</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold">
                      {parceiro.taxa_conversao_leads_propostas}%
                    </div>
                    <TrendingUp className="h-6 w-6 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Taxa Lead → Contrato</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold">
                      {parceiro.taxa_conversao_leads_contratos}%
                    </div>
                    <TrendingUp className="h-6 w-6 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </div>
            </TabsContent>

            {showRedeTab && (
              <TabsContent value="rede" className="mt-6 md:mt-4">
                <ParceiroRedeTab parceiroId={parceiro.parceiro.id} />
              </TabsContent>
            )}

            <TabsContent value="documentos" className="mt-6 md:mt-4">
              <ParceiroDocumentosTab parceiroId={parceiro.parceiro.id} />
            </TabsContent>

            <TabsContent value="financeiro" className="mt-6 md:mt-4">
              <ParceiroFinanceiroTab parceiroId={parceiro.parceiro.id} />
            </TabsContent>
          </Tabs>
          </div>
        )}

        {/* Modal de Edição */}
        <EditarParceiro
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={handleEditSuccess}
          parceiro={parceiro || null}
        />
      </DialogContent>

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Confirmar Exclusão do Parceiro
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o parceiro <strong>{parceiro?.parceiro?.nome}</strong>?
              <br /><br />
              Esta ação é <strong>irreversível</strong> e irá remover:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>O registro do parceiro</li>
                <li>Todos os documentos associados</li>
                <li>Todo o histórico de performance</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir Parceiro'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog quando parceiro possui leads */}
      <AlertDialog open={hasLeadsDialogOpen} onOpenChange={setHasLeadsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Não é Possível Excluir o Parceiro
            </AlertDialogTitle>
            <AlertDialogDescription>
              O parceiro <strong>{parceiro?.parceiro?.nome}</strong> possui <strong>{leadsCount}</strong> lead(s) relacionado(s).
              <br /><br />
              Para excluir este parceiro, você precisa primeiro:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Excluir todos os leads vinculados ao parceiro, ou</li>
                <li>Transferir os leads para outros parceiros</li>
              </ul>
              <br />
              Após realizar uma dessas ações, você poderá excluir o parceiro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Entendi</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}


